import createSelector from 'lib/createSelector'
import moment from 'moment'

import getOfferListKey from 'lib/offerListKey'
import { buildSearchParamsKey, buildDestinationSearchParamsKey } from 'lib/searchUtils'
import { arrayToObject } from 'lib/arrayUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { getSumExtraGuestsSurcharge } from 'lib/cartReservationUtils'

export interface SelectorParams {
  types?: Array<App.OfferType | App.ExperienceOfferType | App.OfferParentType>;
  filters?: App.OfferListFilters;
}

export const getOfferListKeySelector = createSelector(
  (state, options: SelectorParams) => options.types,
  (state, options: SelectorParams) => options.filters,
  (types, filters) => getOfferListKey({
    ...filters,
    offerTypes: filters?.offerTypes ?? types,
  }),
)

// getOfferList returns an offer list corresponding to the given search
// and filter parameters
export const getOfferList = createSelector(
  (state: App.State) => state.offer.offerLists,
  (state: App.State, options: SelectorParams) => getOfferListKeySelector(state, options),
  (offerLists, key) => offerLists[key],
)

export const getFullOrSummaryOffers = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.offerSummaries,
  (state: App.State) => state.offer.tourV2Offers,
  (offers, offerSummaries, tourV2Offers) => ({
    ...offerSummaries,
    ...offers,
    ...tourV2Offers,
  }),
)

export const getSuggestedDates = createSelector(
  (state: App.State) => state.offer.searchResultMetadata.offerSuggestedDates,
  (state: App.State, flexibleSearchFilterKey: string) => flexibleSearchFilterKey,
  (state: App.State, flexibleSearchFilterKey: string, offerId: string) => offerId,
  (suggestedDates, filterKey, offerId) => suggestedDates?.[filterKey]?.[offerId],
)

export const getOfferMetaData = createSelector(
  (state: App.State) => state.offer.searchResultMetadata.offerMetaData,
  (state: App.State, filterKey: string) => filterKey,
  (state: App.State, filterKey: string, offerId: string) => offerId,
  (offerMetaData, filterKey, offerId) => offerMetaData?.[filterKey]?.[offerId],
)

interface GetOffersBestPricesParams {
  offers: Array<App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary>;
  filters: App.OfferListFilters;
}

export interface OfferBestPriceData {
  bestPrice: number;
  loading: boolean;
  soldOut: boolean;
  duration: number;
  bestPricePackage?: App.Package;
  error?: any;
}

/**
 * Returns "best price" information for the given offers.
 * Price given is the total for all rooms, NOT per room.
 */
export const getOffersBestPrices = createSelector(
  (state: App.State) => state.offer.offerBestPrices,
  (state: App.State) => state.offer.offerPricesLoading,
  (state: App.State) => state.offer.offerPricesErrors,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.offer.offerRatesLoading,
  (state: App.State, params: GetOffersBestPricesParams) => params.offers,
  (state: App.State, params: GetOffersBestPricesParams) => params.filters,
  function combiner(bestPrices, pricesLoading, pricesErrors, bedbankOfferRates, bedbankOfferRatesLoading, offers, filters): Record<string, OfferBestPriceData> {
    const searchKey = buildSearchParamsKey(filters.checkIn, filters.checkOut, filters.rooms)
    const hasDates = !!(filters.checkIn && filters.checkOut)
    return arrayToObject(
      offers,
      (offer) => offer.id,
      (offer): OfferBestPriceData => {
        let duration = hasDates ? moment(filters.checkOut).diff(moment(filters.checkIn), 'days') : undefined

        if (offer.type === OFFER_TYPE_BED_BANK) {
          const rateKey = getBedbankRateKey(filters.rooms, filters.checkIn, filters.checkOut)
          const lowestRate = bedbankOfferRates[offer.id]?.[rateKey]?.[0]
          let loading = bedbankOfferRatesLoading[offer.id]
          if (hasDates && typeof lowestRate === 'undefined' && typeof loading === 'undefined') {
            // If we don't have a rate and haven't even started loading it yet, for all intents and purposes it is "loading"
            loading = true
          }

          const soldOut = hasDates && !lowestRate && !loading
          let bestPrice: number
          if (lowestRate) {
            bestPrice = lowestRate.totals.inclusive + lowestRate.totals.propertyFees
            duration = lowestRate.nights
          }

          return {
            bestPrice,
            duration,
            loading,
            soldOut,
          }
        } else {
          const bestPriceInfo = bestPrices[offer.id]?.[searchKey]
          const available = bestPriceInfo?.available
          const rate = available ? bestPriceInfo.rate : undefined
          const error = pricesErrors[offer.id]?.[searchKey]
          let loading = pricesLoading[offer.id]?.[searchKey]
          if (hasDates && !bestPriceInfo && typeof loading === 'undefined') {
            // If we don't have a rate and haven't even started loading it yet, for all intents and purposes it is "loading"
            loading = true
          }

          const soldOut = hasDates && bestPriceInfo && !available

          let bestPricePackage: App.Package
          if (available && 'packages' in offer) {
            bestPricePackage = offer.packages?.find(pkg => pkg.uniqueKey === rate.packageUniqueKey)
          } else if (!hasDates) {
            bestPricePackage = offer.lowestPricePackage
          }

          let bestPrice: number
          if (bestPricePackage) {
            const roomCount = filters?.rooms.length || 1
            const extraGuestSurcharges = getSumExtraGuestsSurcharge(bestPricePackage, filters.rooms, offer)
            bestPrice = (rate?.price ?? bestPricePackage.price) * roomCount + extraGuestSurcharges
            duration = bestPricePackage.duration
          }

          return {
            bestPrice,
            duration,
            loading,
            error,
            soldOut,
            bestPricePackage,
          }
        }
      },
    )
  },
)

export const getOfferAvailabilitySelector = createSelector(
  (state: App.State) => state.offer.searchResultMetadata.offerAvailabilityFromSearchTarget,
  (state, options: SelectorParams) => options.filters,
  (offerAvailabilities, filters: App.OfferListFilters) => {
    const availabilityKey = buildDestinationSearchParamsKey(filters.destinationId, filters.checkIn, filters.checkOut, filters.rooms)
    return offerAvailabilities?.[availabilityKey] ?? {}
  },
)

export const getAvailableOfferListFilters = createSelector(
  (state: App.State) => state.offer.offerListFilterOptions,
  getOfferListKeySelector,
  (offerListFilterOptions, key) => offerListFilterOptions[key],
)
